'use client';

import posthog from 'posthog-js';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { type EventContext, mergeEnvelope } from '@/lib/analytics/context';
import { type AnalyticsEventName, AnalyticsEvents } from '@/lib/analytics/events';
import { type EventManifestKey, type EventName, type EventProps } from '@/lib/analytics/manifest';
import { logger } from '@/lib/logger';
import { filterKnownExceptionNoise } from '@/lib/posthog-exception-filter';
import { redactAutocaptureEvent } from '@/lib/posthog-redaction';
import { isPWA } from '@/lib/pwa';

interface PostHogContextValue {
  posthog: typeof posthog | null;
  isInitialized: boolean;
}

const PostHogContext = createContext<PostHogContextValue>({
  posthog: null,
  isInitialized: false,
});

interface PostHogProviderProps {
  children: React.ReactNode;
}

export function PostHogProvider({ children }: PostHogProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    // NEXT_PUBLIC_POSTHOG_HOST stays the absolute PostHog URL — do NOT repoint
    // it at /ingest. It has absolute-URL server consumers (posthog-node egress
    // in posthog-server.ts, sourcemap upload in next.config.mjs, the
    // delete-user admin route). On the client it acts only as an init feature
    // switch; the network target is the /ingest proxy below.
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
    const isDevelopment = process.env.NODE_ENV === 'development';

    if (typeof window !== 'undefined' && apiKey && host) {
      try {
        posthog.init(apiKey, {
          // Same-origin reverse proxy (next.config.mjs rewrites) so
          // ad-blockers that blocklist *.posthog.com can't drop traffic.
          // Relative value resolves against the current origin, covering both
          // the app and marketing hosts; the SDK loads recorder.js/surveys.js
          // from /ingest/static/* through the same proxy.
          api_host: '/ingest',
          // With a proxied api_host the SDK can't derive the PostHog app URL,
          // so point toolbar/app links at the real host explicitly.
          ui_host: 'https://us.posthog.com',
          person_profiles: 'always',
          // www and app each keep their own localStorage but share the
          // .loristry.com cookie. By default the SDK lets localStorage win, so a
          // returning user's www tab keeps an old anonymous distinct_id (and a
          // fresh session) after they identify on app. Let the shared cookie
          // win instead. Set explicitly rather than via `defaults: '2026-08-29'`,
          // which would also change pageview capture and other defaults.
          cookieWinsOnConflict: true,

          // Enable error tracking in production/staging only
          capture_pageview: true,
          capture_pageleave: true,
          // Autocapture is enabled in all environments. $el_text and attr__aria-label
          // are unconditionally stripped from all autocapture-family events via the
          // before_send handler — see posthog-redaction.ts.
          autocapture: true,

          // Enable automatic exception capture
          capture_exceptions: !isDevelopment,

          // Two-stage pipeline:
          //   1. redactAutocaptureEvent strips $el_text / attr__aria-label PII
          //      from autocapture-family events.
          //   2. filterKnownExceptionNoise drops $exception events matching the
          //      known-benign denylist (see posthog-exception-filter.ts).
          before_send: (rawEvent) => filterKnownExceptionNoise(redactAutocaptureEvent(rawEvent)),

          // Enable session recording for error replay (production only)
          ...(isDevelopment
            ? {}
            : {
                session_recording: {
                  maskAllInputs: false,
                  maskInputOptions: {
                    password: true,
                    email: true,
                    tel: true,
                  },
                  // Also mask elements with .sensitive class
                  maskTextSelector: '.sensitive',
                },
              }),

          loaded: (_posthog) => {
            if (isDevelopment) {
              logger.debug('PostHog initialized (error tracking disabled in development)');
            }
            // Register PWA detection property on all events for analytics segmentation
            _posthog.register({ is_pwa: isPWA() });
            // Set initialized state in loaded callback to avoid synchronous setState in effect
            setIsInitialized(true);
          },
        });
      } catch (error) {
        logger.error('PostHog init failed', { error });
      }
    }
  }, []);

  return (
    <PostHogContext.Provider value={{ posthog: isInitialized ? posthog : null, isInitialized }}>
      {children}
    </PostHogContext.Provider>
  );
}

export const usePostHog = () => {
  const context = useContext(PostHogContext);
  if (context === undefined) {
    throw new Error('usePostHog must be used within a PostHogProvider');
  }
  return context;
};

// Helper hook for tracking events
export const useTrackEvent = () => {
  // Destructure posthog and isInitialized as primitives — NOT the whole context
  // object. PostHogProvider constructs { posthog, isInitialized } inline in its
  // value prop, so depending on that whole object would defeat the memo.
  // Both primitives flip atomically at init (posthog: null → singleton,
  // isInitialized: false → true), so they are stable memo deps.
  const { posthog, isInitialized } = usePostHog();

  return useMemo(
    () => ({
      isInitialized,
      track: (event: AnalyticsEventName, properties?: Record<string, unknown>) => {
        if (posthog) {
          posthog.capture(event, properties);
        }
      },
      identify: (userId: string, properties?: Record<string, unknown>) => {
        if (posthog) {
          posthog.identify(userId, properties);
        }
      },
      // Super-property registration — same primitive as the `is_pwa` / `subscription_status`
      // calls above (posthog.tsx:94, posthog-identify.tsx), exposed for callers that need to
      // capture a durable property at the moment it's known rather than per-event. Registered
      // properties auto-attach to every subsequent capture, so they survive state loss that
      // would otherwise drop a per-event prop (route remounts, attempt_id regeneration).
      register: (props: Record<string, unknown>) => {
        if (posthog) {
          posthog.register(props);
        }
      },
      alias: (alias: string) => {
        if (posthog) {
          posthog.alias(alias);
        }
      },
      // Manual exception capture — for caught exceptions needing extra context.
      // Uncaught exceptions are automatically captured when capture_exceptions: true.
      // Use the SDK's captureException so PostHog gets a structured $exception_list;
      // posthog.capture('$exception', {...}) misses that field and ingestion rejects it.
      captureException: (error: Error, additionalContext?: Record<string, unknown>) => {
        if (posthog) {
          posthog.captureException(error, additionalContext);
        }
      },
      // Typed event capture — enforces manifest shape at compile time.
      // Use this for new call sites; legacy `track` remains for existing callers.
      trackEvent: <E extends EventManifestKey>(
        event: E,
        props: EventProps<E>,
        context: EventContext
      ) => {
        if (!posthog) return;
        const name = AnalyticsEvents[event];
        if (!name) {
          // Should never fire: manifest.test.ts enforces at compile time that every
          // EventManifestKey maps to a matching AnalyticsEvents value.
          logger.error('[Analytics] Unknown event key passed to trackEvent', { event });
          return;
        }
        posthog.capture(name as EventName<E>, mergeEnvelope(props, context));
      },
    }),
    [posthog, isInitialized]
  );
};
